/**
 * Public quiz API — no auth required.
 * Targets /v3/quiz/public/... routes.
 */
import axios from 'axios'

const client = axios.create({
  baseURL: process.env.REACT_APP_ASSESSMENT_API_URL,
})

export interface StartResult {
  attemptId: string
  candidateId: string
  isResuming: boolean
  assessmentName?: string
}

export interface QuestionOption {
  key: string
  text: string
  allowNote?: boolean
}

export interface NextQuestionResult {
  question: {
    _id: string
    title: string
    options: QuestionOption[]
    answerIndex: number
    competenceCode?: string
    proficiencyLevel?: string
    type?: string
    aiLabel?: string
  }
  competenceId: string
  level: string
  outcomeType: string
}

export interface TypeLevelBelief {
  level: string
  p: number
  alpha: number
  beta: number
  hasLo: boolean
  aiExplicitCount: number
}

export interface TypeProgressData {
  type: string
  estimatedLevel: string
  score: number | null
  confidence: number
  directAnswerCount: number
  isInferred?: boolean
  probeLower: number
  probeUpper: number
  nextProbeLevel: string | null
  levels: TypeLevelBelief[]
  aiExplicit?: boolean
}

export interface CompetenceProgress {
  code: string
  competenceId: string
  estimatedLevel: string
  score: number | null
  confidence: number
  directAnswerCount: number
  types: TypeProgressData[]
}

export interface AreaProgress {
  areaCode: string
  estimatedLevel: string | null
  score: number | null
  confidence: number
  competences: CompetenceProgress[]
}

export interface ProgressResult {
  areas: AreaProgress[]
  overallScore: number | null
  kScore: number | null
  sScore: number | null
  aScore: number | null
}

export interface ScoreRegressionWarning {
  competenceId: string
  type: string
  prevScore: number
  newScore: number
  prevAlphas: number[]
  newAlphas: number[]
  testedLoCode: string
  testedCompetenceId: string
  testedType: string
  testedLevel: string
}

export interface SubmitAnswerResult {
  regressions: ScoreRegressionWarning[]
}

export interface AssessmentSummary {
  slug: string
  name: string
  description: string | null
  shortDescription: string | null
}

export interface AttemptInfo {
  assessmentSlug: string | null
  assessmentName: string | null
  status: string
  createdAt: string
  completedAt: string | null
  totalQuestions: number
  resultCode: string | null
  candidate: { name: string; firstName?: string; lastName?: string; email: string; role: string | null } | null
}

export const PublicQuizAPI = {
  listAssessments: async (): Promise<AssessmentSummary[]> => {
    const res = await client.get('/v3/quiz/public/assessments')
    return res.data
  },

  start: async (params: {
    name: string
    firstName?: string
    lastName?: string
    email: string
    role?: string
    assessmentSlug: string
  }): Promise<StartResult> => {
    const res = await client.post('/v3/quiz/public/start', params)
    return res.data
  },

  fetchInfo: async (slug: string): Promise<{ name: string; description?: string; shortDescription?: string; heroText?: string; questionCap?: number | null }> => {
    const res = await client.get(`/v3/quiz/public/assessment/${slug}`)
    return res.data
  },

  nextQuestion: async (attemptId: string, adminToken?: string | null, autoMode?: boolean): Promise<NextQuestionResult | null> => {
    const params = new URLSearchParams()
    if (adminToken) params.set('admin', adminToken)
    if (autoMode) params.set('auto', '1')
    const qs = params.toString()
    const url = `/v3/quiz/public/attempts/${attemptId}/next${qs ? `?${qs}` : ''}`
    const res = await client.get(url)
    return res.data
  },

  submitAnswer: async (
    attemptId: string,
    questionId: string,
    selectedOptionKeys: string[],
  ): Promise<SubmitAnswerResult> => {
    const res = await client.post(`/v3/quiz/public/attempts/${attemptId}/answer`, { questionId, selectedOptionKeys })
    return res.data
  },

  complete: async (attemptId: string, domain?: string): Promise<void> => {
    await client.post(`/v3/quiz/public/attempts/${attemptId}/complete`, { domain })
  },

  getProgress: async (attemptId: string): Promise<ProgressResult> => {
    const res = await client.get(`/v3/quiz/public/attempts/${attemptId}/progress`)
    return res.data
  },

  getAttemptInfo: async (attemptId: string): Promise<AttemptInfo> => {
    const res = await client.get(`/v3/quiz/public/attempts/${attemptId}/info`)
    return res.data
  },
}
